import React, { useEffect, useState, useRef } from "react";
import { toast } from "sonner";
import { ShieldCheck, BadgeCheck, Upload, Clock } from "lucide-react";
import api from "../lib/api";
import { SectionTitle, GoldButton, GhostButton } from "../components/common";

function Card({ kind, title, desc, done, onUpload, busy }) {
  const ref = useRef();
  return (
    <div className="rounded-2xl border border-white/10 bg-[#141113]/60 p-6">
      <div className="flex items-center gap-3 mb-3">
        <div className={`h-11 w-11 rounded-full flex items-center justify-center ${done ? "bg-[#2E7D52]/20 text-[#6FD79E]" : "bg-[#541224]/50 text-[#D4AF37]"}`}>{kind === "id" ? <ShieldCheck size={22} /> : <BadgeCheck size={22} />}</div>
        <div><h3 className="font-serif text-xl text-[#FBF8F3]">{title}</h3>{done && <span className="text-xs text-[#6FD79E]">Verified</span>}</div>
      </div>
      <p className="text-sm text-[#9E978E] mb-4">{desc}</p>
      <input ref={ref} type="file" accept="image/*" hidden onChange={(e) => e.target.files[0] && onUpload(kind, e.target.files[0])} data-testid={`verify-${kind}-input`} />
      {!done && <GoldButton data-testid={`verify-${kind}-btn`} onClick={() => ref.current.click()} disabled={busy} className="w-full flex items-center justify-center gap-2"><Upload size={15} /> {busy ? "Uploading…" : "Upload"}</GoldButton>}
    </div>
  );
}

export default function Verify() {
  const [status, setStatus] = useState(null);
  const [busy, setBusy] = useState(null);
  const load = async () => { const { data } = await api.get("/verification/status"); setStatus(data); };
  useEffect(() => { load(); }, []);

  const upload = async (kind, file) => {
    setBusy(kind);
    try {
      const fd = new FormData(); fd.append("file", file);
      await api.post(`/verification/submit?kind=${kind}`, fd, { headers: { "Content-Type": "multipart/form-data" } });
      toast.success("Submitted for review");
      load();
    } catch (e) { toast.error(e.response?.data?.detail || "Upload failed"); } finally { setBusy(null); }
  };

  const recStatus = status?.record?.status;

  return (
    <div className="max-w-3xl mx-auto px-4 sm:px-6 py-8">
      <SectionTitle eyebrow="Trust & Safety" title="Get Verified" className="mb-3" />
      <p className="text-sm text-[#9E978E] mb-6">Verified members earn a badge and build trust. Your documents are stored securely and are never shown on your public profile.</p>

      {recStatus === "PENDING" && <div className="rounded-xl bg-[#C89528]/15 border border-[#C89528]/40 text-[#E0B84A] px-4 py-3 mb-6 flex items-center gap-2 text-sm"><Clock size={16} /> Your submission is under review.</div>}
      {recStatus === "REJECTED" && <div className="rounded-xl bg-[#8B263E]/20 border border-[#8B263E]/40 text-[#E58CA0] px-4 py-3 mb-6 text-sm">Your verification was not approved. Please re-submit clearer documents.</div>}

      <div className="grid sm:grid-cols-2 gap-5">
        <Card kind="selfie" title="Selfie Verification" desc="Take a clear selfie to confirm you're a real person." done={status?.selfie_verified} onUpload={upload} busy={busy === "selfie"} />
        <Card kind="id" title="ID Verification" desc="Upload a government-issued ID. Stored privately with strict access controls." done={status?.id_verified} onUpload={upload} busy={busy === "id"} />
      </div>
    </div>
  );
}
